// ไฟล์: frontend/src/pages/UserProfilePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import ChangePasswordModal from '../components/common/ChangePasswordModal';
import './UserProfilePage.css';

const UserProfilePage = ({ user, updateUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    school: '',
    subject_area: '',
    bio: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // State สำหรับ Modal เปลี่ยนรหัสผ่าน
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // ตั้งค่าข้อมูลฟอร์มจากข้อมูลผู้ใช้
    setFormData({
      fullname: user.fullname || '',
      email: user.email || '',
      school: user.school || '',
      subject_area: user.subject_area || '',
      bio: user.bio || ''
    });

    // ตั้งค่า preview รูปโปรไฟล์
    if (user.profile_image) {
      setProfileImagePreview(`${process.env.REACT_APP_API_URL}/uploads/profiles/${user.profile_image}`);
    //   setProfileImagePreview(`https://teachershare.backend.kruwirat.com/api/uploads/profiles/${user.profile_image}`);
    //   setProfileImagePreview(`http://localhost:5000/api/uploads/profiles/${user.profile_image}`);
    }

    setLoading(false);
  }, [user, navigate]);

  // จัดการการเปลี่ยนแปลงข้อมูลในฟอร์ม
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // ล้างข้อผิดพลาดเมื่อผู้ใช้แก้ไขข้อมูล
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  // จัดการการเปลี่ยนแปลงรูปโปรไฟล์
  const handleProfileImageChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setProfileImage(file);
      
      // สร้าง URL สำหรับแสดงตัวอย่างรูปภาพ
      const previewUrl = URL.createObjectURL(file);
      setProfileImagePreview(previewUrl);
    }
  };

  // ตรวจสอบความถูกต้องของข้อมูล
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullname.trim()) {
      newErrors.fullname = 'กรุณาระบุชื่อ-นามสกุล';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'กรุณาระบุอีเมล';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ส่งข้อมูลฟอร์ม
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบความถูกต้องของข้อมูล
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      // อัพเดทข้อมูลโปรไฟล์
      await axios.put('/users/update', formData);
      
      // อัพโหลดรูปโปรไฟล์ (ถ้ามี)
      if (profileImage) {
        const formDataForImage = new FormData();
        formDataForImage.append('profile_image', profileImage);
        
        const uploadResponse = await axios.post('/users/upload-profile', formDataForImage, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // อัพเดท profile_image ในข้อมูลผู้ใช้
        updateUser({ profile_image: uploadResponse.data.profile_image });
      }
      
      // อัพเดทข้อมูลผู้ใช้ในส่วนของแอป
      updateUser(formData);
      
      Swal.fire({
        icon: 'success',
        title: 'อัพเดทโปรไฟล์สำเร็จ',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: 'ไม่สามารถอัพเดทโปรไฟล์ได้ โปรดลองใหม่อีกครั้ง'
      });
    } finally {
      setSubmitting(false);
    }
  };
  
  // เปิด Modal เปลี่ยนรหัสผ่าน
  const openChangePasswordModal = () => {
    setIsChangePasswordModalOpen(true);
  };
  
  // ปิด Modal เปลี่ยนรหัสผ่าน
  const closeChangePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title"><i className="fas fa-user-circle"></i> โปรไฟล์ของฉัน</h1>
          <p className="page-description">
            จัดการข้อมูลส่วนตัวและโปรไฟล์ของคุณ
          </p>
        </div>
        
        <div className="profile-container">
          <div className="profile-sidebar">
            <div className="profile-image-section">
              <div className="profile-image-container">
                <img 
                  src={profileImagePreview || `https://ui-avatars.com/api/?name=${user.fullname}&background=random&size=200`} 
                  alt={user.fullname} 
                  className="profile-image"
                />
                <label htmlFor="profile_image" className="profile-image-edit">
                  <i className="fas fa-camera"></i>
                </label>
                <input 
                  type="file"
                  id="profile_image"
                  name="profile_image"
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  className="profile-image-input"
                />
              </div>
              <div className="profile-name">{user.fullname}</div>
              <div className="profile-role">{user.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ครูผู้สอน'}</div>
            </div>
            
            <div className="profile-actions">
              <button className="btn btn-primary btn-block" onClick={openChangePasswordModal}>
                <i className="fas fa-key"></i> เปลี่ยนรหัสผ่าน
              </button>
            </div>
            
            <div className="profile-tips">
              <div className="tip-title"><i className="fas fa-lightbulb"></i> คำแนะนำ</div>
              <ul className="tip-list">
                <li>อัพเดทข้อมูลโปรไฟล์ให้เป็นปัจจุบัน</li>
                <li>เลือกรูปโปรไฟล์ที่มองเห็นได้ชัดเจน</li>
                <li>เพิ่มข้อมูลเกี่ยวกับโรงเรียนและวิชาที่สอนเพื่อให้เพื่อนครูรู้จักคุณมากขึ้น</li>
                <li>เปลี่ยนรหัสผ่านเป็นประจำเพื่อความปลอดภัย</li>
              </ul>
            </div>
          </div>
          
          <div className="profile-content">
            <div className="profile-card">
              <div className="profile-card-header">
                <h2><i className="fas fa-user-edit"></i> แก้ไขข้อมูลส่วนตัว</h2>
              </div>
              <div className="profile-card-body">
                <form onSubmit={handleSubmit} className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="fullname" className="form-label required">ชื่อ-นามสกุล</label>
                      <input
                        type="text"
                        id="fullname"
                        name="fullname"
                        className={`form-control ${errors.fullname ? 'is-invalid' : ''}`}
                        value={formData.fullname}
                        onChange={handleChange}
                        placeholder="ระบุชื่อ-นามสกุล"
                      />
                      {errors.fullname && <div className="invalid-feedback">{errors.fullname}</div>}
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email" className="form-label required">อีเมล</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="ระบุอีเมล"
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="school" className="form-label">โรงเรียน</label>
                      <input
                        type="text"
                        id="school"
                        name="school"
                        className="form-control"
                        value={formData.school}
                        onChange={handleChange}
                        placeholder="ระบุโรงเรียนที่สอน"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="subject_area" className="form-label">วิชาที่สอน</label>
                      <input
                        type="text"
                        id="subject_area"
                        name="subject_area"
                        className="form-control"
                        value={formData.subject_area}
                        onChange={handleChange}
                        placeholder="ระบุวิชาที่สอน"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="bio" className="form-label">เกี่ยวกับฉัน</label>
                    <textarea
                      id="bio"
                      name="bio"
                      className="form-control"
                      value={formData.bio}
                      onChange={handleChange}
                      rows="4"
                      placeholder="เล่าเกี่ยวกับตัวคุณ ประสบการณ์ ความเชี่ยวชาญ หรือความสนใจ"
                    ></textarea>
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" className="btn btn-light" onClick={() => navigate(-1)}>
                      <i className="fas fa-arrow-left"></i> ย้อนกลับ
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> กำลังบันทึก...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save"></i> บันทึกข้อมูล
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal เปลี่ยนรหัสผ่าน */}
      <ChangePasswordModal 
        isOpen={isChangePasswordModalOpen} 
        onClose={closeChangePasswordModal} 
      />
    </div>
  );
};

export default UserProfilePage;
